import { Reducer } from "redux";

import { EnvironmentID } from "../types/global";

export const SET_ACCOUNT_METADATA = "SET_ACCOUNT_METADATA";
export const SET_CURRENT_ENVIRONMENT = "SET_CURRENT_ENVIRONMENT";

export const LOCAL_STORE_CURRENT_ENV = "currentEnvironment";
export const MOST_RECENT_ENV = "mostRecentEnv";

export const SET_PHI_ALLOW_LIST_SAVING = "SET_PHI_ALLOW_LIST_SAVING";
export const SET_PHI_ALLOW_LIST_SAVED = "SET_PHI_ALLOW_LIST_SAVED";

export const SET_ACCOUNT_METADATA_UPDATING = "SET_ACCOUNT_METADATA_UPDATING";

export type ProjectID = string;
export type ProjectCodeEval = {
  onPageLoad?: string;
  onPageChange?: string;
};
export type ProjectBase = {
  name: string;
};
export type LocalStorageProject = ProjectBase & {
  ID: ProjectID;
};
export type EnvironmentBase = {
  name: string;
  apiHost?: string;
  appHost?: string;
};
export type LocalStorageEnvironment = EnvironmentBase & {
  ID: EnvironmentID;
  project: LocalStorageProject;
};
export type LoadedEnvironment = EnvironmentBase & {
  ID: EnvironmentID;
  projectID: ProjectID;
};
export type EditableEnvironment = EnvironmentBase & {
  ID: EnvironmentID | null;
  projectID: ProjectID | null;
  deletable: boolean;
};
export type LoadedProject = LocalStorageProject & {
  environments: LoadedEnvironment[];
  codeEval?: ProjectCodeEval;
};
export type EditableProject = ProjectBase & {
  ID: ProjectID | null;
  environments: EditableEnvironment[];
  codeEval?: ProjectCodeEval;
};

export type AccountConfig = {
  hipaa?: boolean;
  personas?: boolean;
};

export type AccountMetadata = {
  config: AccountConfig;
  projects: Array<EditableProject>;
  hasObjectSources: boolean;
};

class JSONValueStorage<T> {
  storage: Storage;
  key: string;

  constructor(location: Storage, key: string) {
    this.storage = location;
    this.key = key;
  }

  set(objValue: T | null) {
    this.storage.setItem(this.key, JSON.stringify(objValue));
  }

  get(): T | null {
    const val = this.storage.getItem(this.key);
    if (!val) {
      return null;
    }

    try {
      return JSON.parse(val);
    } catch (e) {
      return null;
    }
  }
}

class FPEnvironmentLocker {
  currentEnvStorage: JSONValueStorage<string>;
  mostRecentEnvStorage: JSONValueStorage<string>;
  accountMetadata: AccountMetadata | null;

  constructor() {
    // We keep the current env in sessionStorage, but keep track of the most recently
    // used env in localStorage to use when a tab first loads.
    this.currentEnvStorage = new JSONValueStorage(
      window.sessionStorage,
      LOCAL_STORE_CURRENT_ENV
    );
    this.mostRecentEnvStorage = new JSONValueStorage(
      window.localStorage,
      MOST_RECENT_ENV
    );
    this.accountMetadata = null;
  }

  setCurrentEnv(currentEnvID: string) {
    this.setMostRecentEnv(currentEnvID);
    this.currentEnvStorage.set(currentEnvID);
  }

  setMostRecentEnv(currentEnvID: string) {
    this.mostRecentEnvStorage.set(currentEnvID);
  }

  getCurrentEnvID(): string | null {
    return this.currentEnvStorage.get();
  }

  getRecentEnvID(): string | null {
    return this.mostRecentEnvStorage.get();
  }

  getEnvByID(envID: string | null): LocalStorageEnvironment | null {
    if (envID && this.accountMetadata) {
      for (let project of this.accountMetadata.projects) {
        for (let env of project.environments) {
          if (env.ID === envID) {
            return {
              ...env,
              // @ts-ignore
              project: project,
            };
          }
        }
      }
    }

    return null;
  }

  getDefaultEnv(): LocalStorageEnvironment | null {
    // Just get the first env in the first project
    if (
      this.accountMetadata &&
      this.accountMetadata.projects &&
      this.accountMetadata.projects.length &&
      this.accountMetadata.projects[0] &&
      this.accountMetadata.projects[0].environments &&
      this.accountMetadata.projects[0].environments.length
    ) {
      const proj = this.accountMetadata.projects[0];
      const env = proj.environments[0];
      return {
        ...env,
        // @ts-ignore
        project: proj,
      };
    }
    return null;
  }

  /** Gets the environment to use on the page. In order try:
   * - Current Env (sessionStorage)
   * - Most recently used Env (localStorage)
   * - Default Env
   */
  getCurrentEnv(): LocalStorageEnvironment | null {
    if (this.accountMetadata) {
      return (
        this.getEnvByID(this.getCurrentEnvID()) ||
        this.getEnvByID(this.getRecentEnvID()) ||
        this.getDefaultEnv()
      );
    }

    return null;
  }

  initialize(accountMetadata: AccountMetadata) {
    this.accountMetadata = accountMetadata;

    if (this.getEnvByID(this.getCurrentEnvID())) {
      return;
    }

    // If selecting the recent env, we now need to set this env as the current env.
    const recentEnv = this.getEnvByID(this.getRecentEnvID());
    if (recentEnv) {
      this.setCurrentEnv(recentEnv.ID);
    }

    // If selecting the default env, we now need to set this env as the current env.
    const defaultEnv = this.getDefaultEnv();
    if (defaultEnv) {
      this.setCurrentEnv(defaultEnv.ID);
    }
  }
}

export const envLocker = new FPEnvironmentLocker();

export type EnvironmentsState = {
  accountMetadata: AccountMetadata;
  loaded: boolean;
  currentEnvironment: LocalStorageEnvironment | null; // This allows us to always have the ability to get the currentEnv directly from redux
};

export const initialEnvironmentsState: EnvironmentsState = {
  accountMetadata: {
    // Default HIPAA to true until we load the actual config. Do this so if we
    // depend on the HIPAA flag, we always err on the safer side.
    config: { hipaa: true },
    projects: [],
    hasObjectSources: false,
  },
  loaded: false,
  currentEnvironment: null,
};

// Actions
type SetAccountMetadataAction = {
  type: typeof SET_ACCOUNT_METADATA;
  payload: AccountMetadata;
};

type SetCurrentEnvironmentAction = {
  type: typeof SET_CURRENT_ENVIRONMENT;
  payload: string;
};

type SetAccountMetadataSavingAction = {
  type: typeof SET_ACCOUNT_METADATA_UPDATING;
}

type EnvironmentActions =
  | SetAccountMetadataAction
  | SetCurrentEnvironmentAction
  | SetAccountMetadataSavingAction;

const environmentsReducer: Reducer<EnvironmentsState, EnvironmentActions> = (
  state = initialEnvironmentsState,
  action
): EnvironmentsState => {
  switch (action.type) {
    case SET_ACCOUNT_METADATA:
      envLocker.initialize(action.payload);
      return {
        ...state,
        accountMetadata: action.payload,
        loaded: true,
        currentEnvironment: envLocker.getCurrentEnv(),
      };
    case SET_CURRENT_ENVIRONMENT:
      envLocker.setCurrentEnv(action.payload);
      return {
        ...state,
        currentEnvironment: envLocker.getCurrentEnv(),
      };
    case SET_ACCOUNT_METADATA_UPDATING:
      return {
        ...state,
        loaded: false,
      }
    default:
      return state;
  }
};

export default environmentsReducer;
